import { QSGState, Action } from 'src/store/types';
import { QSGActions } from './actions';
import { normalizeSectionId } from '../../../utils/query-param-helpers';
import { SectionModel } from '../../../fetch-data/graphql/types';
import { normalizeNavigation } from '../../../utils/navigation';

const initialState: QSGState = {
  availableModels: [],
  selectedModel: {
    modelId: '',
    modelName: '',
    modelYear: '',
  },
  qsgNavigation: null,
  qsgContent: null,
  qsgSectionMap: {},
  qsgSectionLoading: {},
  qsgSectionErrors: {},
  qsgSearchTerm: '',
  qsgSearchPage: 1,
  qsgSearchResultsMap: {},
  qsgSearchLoading: {},
  qsgSearchErrors: {},
  notificationMessage: '',
  allDisclaimers: [],
  ssrHistory: null,
};

export const qsgReducer = (
  state: QSGState = initialState,
  action: Action
): QSGState => {
  switch (action.type) {
    case QSGActions.SET_AVAILABLE_QSG_MODELS:
      return { ...state, availableModels: action.payload };
    case QSGActions.SET_QSG_CONTENT:
      return { ...state, qsgContent: action.payload };
    case QSGActions.SET_SELECTED_QSG_MODEL:
      return {
        ...state,
        selectedModel: { ...state.selectedModel, ...action.payload },
      };
    case QSGActions.SET_QSG_NAVIGATION:
      // Normalize all sectionIds in the navigation to ensure consistent lookups
      const normalizedNavigation = normalizeNavigation(action.payload);
      return { ...state, qsgNavigation: normalizedNavigation };
    case QSGActions.SET_QSG_SECTION:
      // Store section with normalized key for consistent lookups
      // Preserve the original sectionId as originalSectionId
      const normalizedSectionId = normalizeSectionId(action.payload.sectionId);
      const originalSectionId = action.payload.sectionId;
      return {
        ...state,
        qsgSectionMap: {
          ...state.qsgSectionMap,
          [normalizedSectionId]: {
            ...action.payload,
            sectionId: normalizedSectionId,
            originalSectionId,
          },
        },
        qsgSectionLoading: {
          ...state.qsgSectionLoading,
          [normalizedSectionId]: false,
        },
        qsgSectionErrors: {
          ...state.qsgSectionErrors,
          [normalizedSectionId]: null,
        },
      };
    case QSGActions.SET_ALL_DISCLAIMERS:
      return {
        ...state,
        allDisclaimers: action.payload,
      };
    case QSGActions.SET_QSG_SECTION_LOADING:
      // Normalize section ID for consistent lookups
      const normalizedLoadingSectionId = normalizeSectionId(
        action.payload.sectionId
      );
      return {
        ...state,
        qsgSectionLoading: {
          ...state.qsgSectionLoading,
          [normalizedLoadingSectionId]: action.payload.isLoading,
        },
      };
    case QSGActions.SET_QSG_SECTION_ERROR:
      // Normalize section ID for consistent lookups
      const normalizedErrorSectionId = normalizeSectionId(
        action.payload.sectionId
      );
      return {
        ...state,
        ...(action.payload.error
          ? {
              qsgSectionLoading: {
                ...state.qsgSectionLoading,
                [normalizedErrorSectionId]: false,
              },
            }
          : {}),
        qsgSectionErrors: {
          ...state.qsgSectionErrors,
          [normalizedErrorSectionId]: action.payload.error,
        },
      };
    case QSGActions.RESET_QSG_MODEL_SELECTION:
      return {
        ...state,
        selectedModel: {
          modelId: '',
          modelName: '',
          modelYear: '',
        },
      };
    case QSGActions.RESET_QSG_DATA:
      return {
        ...state,
        qsgContent: null,
        qsgNavigation: null,
        qsgSectionMap: {},
        qsgSectionLoading: {},
        qsgSectionErrors: {},
        qsgSearchTerm: '',
        qsgSearchPage: 1,
        qsgSearchResultsMap: {},
        qsgSearchLoading: {},
        qsgSearchErrors: {},
      };
    case QSGActions.SET_NOTIFICATION_MESSAGE:
      return {
        ...state,
        notificationMessage: action.payload,
      };
    case QSGActions.SET_QSG_SEARCH_TERM:
      return {
        ...state,
        qsgSearchTerm: action.payload,
        //qsgSearchPage: 1,
      };
    case QSGActions.SET_QSG_SEARCH_PAGE:
      return {
        ...state,
        qsgSearchPage: action.payload,
      };
    case QSGActions.RESET_QSG_SEARCH:
      return {
        ...state,
        qsgSearchTerm: '',
        qsgSearchPage: 1,
        qsgSearchResultsMap: {},
        qsgSearchLoading: {},
        qsgSearchErrors: {},
      };
    case QSGActions.SET_QSG_SEARCH_RESULTS:
      return {
        ...state,
        qsgSearchResultsMap: {
          ...state.qsgSearchResultsMap,
          [action.payload.searchKey]: action.payload.results,
        },
        qsgSearchLoading: {
          ...state.qsgSearchLoading,
          [action.payload.searchKey]: false,
        },
        qsgSearchErrors: {
          ...state.qsgSearchErrors,
          [action.payload.searchKey]: null,
        },
      };
    case QSGActions.SET_QSG_SEARCH_LOADING:
      return {
        ...state,
        qsgSearchLoading: {
          ...state.qsgSearchLoading,
          [action.payload.searchKey]: action.payload.isLoading,
        },
      };
    case QSGActions.SET_QSG_SEARCH_ERROR:
      return {
        ...state,
        ...(action.payload.error
          ? {
              qsgSearchLoading: {
                ...state.qsgSearchLoading,
                [action.payload.searchKey]: false,
              },
            }
          : {}),
        qsgSearchErrors: {
          ...state.qsgSearchErrors,
          [action.payload.searchKey]: action.payload.error,
        },
      };
    case QSGActions.SET_QSG_SECTION_MAP:
      // Normalize all section IDs in the map to ensure consistent lookups
      // Also update sectionId in each section object to match the normalized key
      // Preserve the original sectionId as originalSectionId
      const sectionMap = action.payload as Record<string, SectionModel>;
      const normalizedSectionMap: Record<string, SectionModel> = {};
      for (const [sectionId, section] of Object.entries(sectionMap)) {
        const normalizedId = normalizeSectionId(sectionId);
        const originalSectionId = section.sectionId;
        normalizedSectionMap[normalizedId] = {
          ...section,
          sectionId: normalizedId, // Update sectionId in the object to match the key
          originalSectionId, // Preserve the original sectionId
        };
      }
      return {
        ...state,
        qsgSectionMap: normalizedSectionMap,
      };
    case QSGActions.SET_SSR_HISTORY:
      return {
        ...state,
        ssrHistory: action.payload,
      };
    default:
      return state;
  }
};

export const getQSGInitialState = (): QSGState => initialState;
